// Uploads files to DigitalOcean Spaces (S3-compatible) via a hand-signed AWS
// SigV4 PUT request, using the browser's native Web Crypto API. Ported from
// appbot_frontend's S3FileManager class, but reimplemented without the
// `aws-sdk` package (couldn't be installed here — see [[chain-logos-hotlink-fix]]
// style note: node_modules in this environment is root-owned) and without a
// backend (this is a pure static frontend, so there's no server to hold the
// credentials — same exposure the original appbot_frontend class already has).
// Signing algorithm validated against the real bucket before shipping.
const REGION = process.env.REACT_APP_DO_SPACES_REGION;
const BUCKET = process.env.REACT_APP_DO_SPACES_BUCKET;
const ENDPOINT_HOST = process.env.REACT_APP_DO_SPACES_ENDPOINT;
const ACCESS_KEY = process.env.REACT_APP_DO_SPACES_KEY;
const SECRET_KEY = process.env.REACT_APP_DO_SPACES_SECRET;

const enc = new TextEncoder();

async function hmac(keyData, msg) {
  const key = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  return crypto.subtle.sign("HMAC", key, enc.encode(msg));
}

function toHex(buf) {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(str) {
  return toHex(await crypto.subtle.digest("SHA-256", enc.encode(str)));
}

// Keeps filenames ASCII-safe so the signed canonical URI always matches what
// the browser actually sends over the wire (no percent-encoding edge cases).
export function sanitizeFileName(name) {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_");
}

// Uploads `file` to DigitalOcean Spaces at `key` (e.g. "articles/slug/cover/x.png")
// with public-read ACL and returns its public URL.
export async function uploadToSpaces(file, key) {
  const host = `${BUCKET}.${ENDPOINT_HOST}`;
  const amzDate = new Date().toISOString().replace(/[:-]|\.\d{3}/g, "");
  const dateStamp = amzDate.slice(0, 8);
  const payloadHash = "UNSIGNED-PAYLOAD";

  const lowerHeaders = {
    host,
    "x-amz-acl": "public-read",
    "x-amz-content-sha256": payloadHash,
    "x-amz-date": amzDate,
  };
  const names = Object.keys(lowerHeaders).sort();
  const canonicalHeaders = names.map((h) => `${h}:${lowerHeaders[h]}\n`).join("");
  const signedHeaders = names.join(";");

  const canonicalRequest = ["PUT", `/${key}`, "", canonicalHeaders, signedHeaders, payloadHash].join(
    "\n",
  );
  const credentialScope = `${dateStamp}/${REGION}/s3/aws4_request`;
  const stringToSign = [
    "AWS4-HMAC-SHA256",
    amzDate,
    credentialScope,
    await sha256Hex(canonicalRequest),
  ].join("\n");

  const kDate = await hmac(enc.encode(`AWS4${SECRET_KEY}`), dateStamp);
  const kRegion = await hmac(kDate, REGION);
  const kService = await hmac(kRegion, "s3");
  const kSigning = await hmac(kService, "aws4_request");
  const signature = toHex(await hmac(kSigning, stringToSign));

  const authorization = `AWS4-HMAC-SHA256 Credential=${ACCESS_KEY}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

  const res = await fetch(`https://${host}/${key}`, {
    method: "PUT",
    headers: {
      "x-amz-acl": "public-read",
      "x-amz-content-sha256": payloadHash,
      "x-amz-date": amzDate,
      "Content-Type": file.type || "application/octet-stream",
      Authorization: authorization,
    },
    body: file,
  });

  if (!res.ok) {
    throw new Error(`DigitalOcean Spaces upload failed: ${res.status} ${await res.text()}`);
  }

  return `https://${host}/${key}`;
}
